'use client';

import { useEffect, useState } from 'react';

import { fetchTypeOeuvreIcons } from '@/lib/actions/fetchTypeOeuvreIcons';
import { logger } from '@/logger';

import {
  type TypeOeuvreIcons,
  TypeOeuvreIconsProvider,
} from './TypeOeuvreIconsContext';

// Module-scope cache: shared across all instances to avoid duplicate fetches.
// On failure, retries after RETRY_DELAY_MS instead of giving up permanently.
const RETRY_DELAY_MS = 60_000;
let cachedIcons: TypeOeuvreIcons | null = null;
let lastFailureTime = 0;

/**
 * Client-side provider that auto-fetches TypeOeuvre icons via server action.
 * Uses module-scope cache to avoid duplicate fetches across multiple modals.
 *
 * Invariants:
 * - Never blocks form rendering — children render immediately
 * - Retries after RETRY_DELAY_MS (60s) on failure
 * - Never causes layout shift (TypeOeuvreSection handles missing icons gracefully)
 */
export function AutoTypeOeuvreIconsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [icons, setIcons] = useState<TypeOeuvreIcons | null>(cachedIcons);

  useEffect(() => {
    if (cachedIcons) {
      setIcons(cachedIcons);
      return;
    }
    if (lastFailureTime > 0 && Date.now() - lastFailureTime < RETRY_DELAY_MS) {
      return;
    }

    let cancelled = false;

    fetchTypeOeuvreIcons()
      .then((result) => {
        if (!cancelled) {
          cachedIcons = result;
          setIcons(result);
        }
      })
      .catch((err) => {
        lastFailureTime = Date.now();
        logger.warn('Failed to fetch TypeOeuvre icons (non-blocking)', err);
      });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, []);

  if (icons) {
    return (
      <TypeOeuvreIconsProvider icons={icons}>
        {children}
      </TypeOeuvreIconsProvider>
    );
  }

  // Render children without provider — TypeOeuvreSection handles null icons gracefully
  return children;
}
